import React, { useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import translate from "../../../i18n/translate";
import ApiRequest from "../../../actions/ApiRequest";

// Reads back the login code a seller was sent but never received.
//
// A code goes out over WhatsApp when someone signs in from a machine we have
// not seen before. When it does not arrive - a dropped WhatsApp session, a
// changed number, a handset with no signal - the seller cannot get in and has
// no way round it. This is how support reads it back to them.
//
// It is effectively a key to that account, so the backend allows admins only
// and writes every lookup to the security log.
const VerificationCodeLookup = ({ isDarktheme }) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();

    const wanted = query.trim();
    if (!wanted) return;

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const res = await ApiRequest.post(
        `get_verification_code?sellerid=${encodeURIComponent(wanted)}`
      );

      if (res?.data?.status === "success") {
        setResult(res.data);
      } else {
        setError(res?.data?.reason || "Something Went Wrong!");
      }
    } catch (err) {
      setError("Something Went Wrong!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="h5" sx={{ marginBottom: 1 }}>
        {translate("Login Code")}
      </Typography>

      <Typography variant="body2" sx={{ marginBottom: 3, opacity: 0.75, maxWidth: "62ch" }}>
        {translate("loginCodeHint")}
      </Typography>

      <Box
        component="form"
        onSubmit={lookup}
        sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", marginBottom: 3 }}
      >
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          label={translate("Seller ID or mobile number")}
          sx={{ minWidth: 260 }}
        />
        <Button type="submit" variant="contained" disabled={loading || !query.trim()}>
          {loading ? <CircularProgress size={20} color="inherit" /> : translate("Search")}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ maxWidth: 620 }}>{error}</Alert>}

      {result && (
        <Paper sx={{ padding: 3, maxWidth: 620 }}>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {result.sellerid} &middot; {result.number}
          </Typography>

          {result.state === "valid" && (
            <>
              <Typography
                sx={{
                  fontSize: 40,
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  fontVariantNumeric: "tabular-nums",
                  marginTop: 1,
                }}
              >
                {result.code}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", marginTop: 1 }}>
                <Chip
                  size="small"
                  color="success"
                  label={
                    result.seconds_left != null
                      ? `${Math.max(0, Math.floor(result.seconds_left / 60))}m ${Math.max(0, result.seconds_left % 60)}s left`
                      : translate("valid")
                  }
                />
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  {translate("issued")} {result.issued_at}
                </Typography>
              </Box>
            </>
          )}

          {result.state === "expired" && (
            <Alert severity="warning" sx={{ marginTop: 2 }}>
              {translate("loginCodeExpired")}
            </Alert>
          )}

          {result.state === "none" && (
            <Alert severity="info" sx={{ marginTop: 2 }}>
              {translate("loginCodeNone")}
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps)(VerificationCodeLookup);
